import { useState, useEffect } from "react";
import { supabase } from "../supabase/supabase";
import { SEASON_YEAR } from "../utils/season";
import { normalize, canonicalizeTeam } from "../utils/scheduleUtils";
import { getCurrentWeekNumber, getBestWeekStats } from "../utils/leagueUtils";
import { isGameLive } from "../utils/gameStatus";
import { getCached, setCached, ttlForGames, markLive, LIVE_TTL_MS, STATIC_TTL_MS } from "../utils/queryCache";
import { maskBlockedMembers, BLOCKED_TEAM_NAME } from "../utils/blocking";

// Keys every game under each of its teams, in every spelling getScheduleEntry
// tries, so a lineup slug resolves whichever form it was stored in.
function buildGamesByTeam(gamesData) {
  const gamesByTeam = {};
  (gamesData || []).forEach((game) => {
    const homeTeam = game.home_team;
    const awayTeam = game.away_team;
    if (!homeTeam || !awayTeam) return;
    // A mid-game weather delay reports the same game_status as a
    // delay before kickoff (ESPN gives both 'delayed'), so a plain
    // equality check against 'in_progress' drops a paused-but-live
    // game to "not started" the moment it's delayed. isGameLive tells
    // them apart via period, which is only set once a game has
    // actually kicked off.
    const hasLiveGame = isGameLive({ gameStatus: game.game_status, period: game.period });
    const homeData = {
      opponent: awayTeam, isHome: true,
      gameStatus: game.game_status || "scheduled",
      gameComplete: game.game_complete || false,
      homeScore: game.home_score || 0, awayScore: game.away_score || 0,
      hasLiveGame,
    };
    const awayData = {
      opponent: homeTeam, isHome: false,
      gameStatus: game.game_status || "scheduled",
      gameComplete: game.game_complete || false,
      homeScore: game.home_score || 0, awayScore: game.away_score || 0,
      hasLiveGame,
    };
    gamesByTeam[homeTeam] = homeData; gamesByTeam[awayTeam] = awayData;
    gamesByTeam[homeTeam.toLowerCase()] = homeData; gamesByTeam[awayTeam.toLowerCase()] = awayData;
    gamesByTeam[normalize(homeTeam)] = homeData; gamesByTeam[normalize(awayTeam)] = awayData;
    gamesByTeam[`__canon__:${canonicalizeTeam(homeTeam)}`] = homeData;
    gamesByTeam[`__canon__:${canonicalizeTeam(awayTeam)}`] = awayData;
  });
  return gamesByTeam;
}

/**
 * Encapsulates all async data-fetching for the MyLeague page.
 *
 * Returns:
 *   members, leagueName, maxManagers, loading,
 *   scheduleData, scheduleLoading,
 *   weeklyStandings, availableWeeks, standingsLoading,
 *   loadWeeklyStandings   ← called by WeekSelector on user interaction
 */
// blockedIds is the caller's own block list (users.id values), passed down
// from LeagueContext. It is applied to the raw member rows before they are
// mapped, and again to anything served from the query cache, because the cache
// key is per league and per week and a block or unblock does not invalidate it.
export function useLeagueData({ leagueId, currentWeek, ctxLeagueData, blockedIds }) {
  const [members, setMembers] = useState([]);
  const [leagueName, setLeagueName] = useState("");
  const [maxManagers, setMaxManagers] = useState(8);
  const [loading, setLoading] = useState(true);

  const [scheduleData, setScheduleData] = useState({});
  const [scheduleLoading, setScheduleLoading] = useState(true);

  const [weeklyStandings, setWeeklyStandings] = useState({});
  const [availableWeeks, setAvailableWeeks] = useState([]);
  const [standingsLoading, setStandingsLoading] = useState(false);

  // A stable primitive for the effect below to depend on. The Set itself is a
  // new object on every LeagueContext load, and the block list arrives one
  // round trip after the first render, so without this the page would render
  // its members before the mask was known and never re-run.
  const blockKey = blockedIds ? [...blockedIds].sort().join(",") : "";


  // ── weekly standings (lazy – called on demand by WeekSelector) ───────────────

  // weekly_standings holds league_id, member_id, week, points, rank, one row
  // per member per week. `points` there is that week's score, not a running
  // total (see the comment on standingsRows in backend/season.js), so a
  // single week's rows are only ever enough for the WK column. The row used
  // to be spread across both the WK and Total columns, which is why Total
  // silently showed that week's score twice and WK showed nothing at all.
  //
  // Total for a past week is the sum of every week's score up to and
  // including it, which this table already has lying around one row per
  // week: no new column, no migration, just fetching the range instead of
  // the single week.
  //
  // It has no user_id and no team_name: this used to read both, so every
  // historical row came back with an undefined id and an undefined team
  // name. member_id is league_members.id, which is exactly what the member
  // objects below use as their `id`, so rows join straight onto the members
  // list for the name and avatar the table has never carried.
  const loadWeeklyStandings = async (week, membersForWeek = members) => {
    if (weeklyStandings[week]) return; // already cached

    // A finished week's standings are final: recalculateAllMemberPoints only
    // ever rewrites the week in play. Those rows are cached to disk, so paging
    // back through the season costs one query per week per day rather than one
    // every time the selector is touched. The current week is never persisted,
    // since its points move with every score that lands.
    //
    // Only the raw rows are cached, not the joined output: names and avatars
    // come from members, which carries the block mask, and a cached joined row
    // would show a blocked manager's name back after they were blocked.
    const isFinished = week < getCurrentWeekNumber(currentWeek);
    const cacheKey = `standings:${leagueId}:week:${week}`;
    const cachedRows = isFinished ? getCached(cacheKey) : null;

    try {
      setStandingsLoading(true);
      // Every week from 1 through the one selected, not just that one row:
      // the running total needs all of them, and this is still a single
      // indexed query.
      const { data: standingsData } = cachedRows
        ? { data: cachedRows }
        : await supabase
          .from("weekly_standings")
          .select("member_id, week, points, rank")
          .eq("league_id", leagueId)
          .lte("week", week);

      // Length-checked: an empty array is truthy, so a failed request or a
      // week whose snapshot never ran was persisted for a day and read back as
      // a real answer, leaving the standings blank with no way to refetch.
      if (isFinished && !cachedRows && standingsData?.length) {
        setCached(cacheKey, standingsData, STATIC_TTL_MS, { persist: true });
      }

      const byMemberId = new Map(
        (membersForWeek || []).map((m) => [m.id, m])
      );

      // One row per member: this week's score and rank come straight off
      // its own row, the total is every week up to and including it summed.
      const byMember = new Map();
      for (const row of standingsData || []) {
        const entry = byMember.get(row.member_id) || { total: 0 };
        entry.total += row.points || 0;
        if (row.week === week) {
          entry.weeklyPoints = row.points;
          entry.rank = row.rank;
        }
        byMember.set(row.member_id, entry);
      }

      const weekStandings = [...byMember.entries()]
        .map(([memberId, entry]) => {
          const member = byMemberId.get(memberId);
          return {
            id: memberId,   // matches member.id, so rank changes resolve
            memberId,
            rank: entry.rank,
            weeklyPoints: entry.weeklyPoints ?? 0,
            points: entry.total,
            teamName: member?.teamName || "Unnamed Team",
            firstName: member?.firstName || "",
            teamAvatar: member?.teamAvatar || null,
          };
        })
        .sort((a, b) => (a.rank || 0) - (b.rank || 0));

      setWeeklyStandings((prev) => ({ ...prev, [week]: weekStandings }));
    } catch (error) {
      console.error(`Error loading Week ${week} standings:`, error);
    } finally {
      setStandingsLoading(false);
    }
  };

  // ── main data load ──────────────────────────────────────────────────────────

  useEffect(() => {
    const fetchData = async () => {
      // ── helpers (defined here so they close over current args without needing deps) ──

      // Returns { gamesByTeam, rawGames } rather than setting state directly,
      // so the caller can cache the built map and read game_status off the raw
      // rows to decide how long that cache should live.
      const fetchScheduleData = async (weekNum) => {
        try {
          setScheduleLoading(true);
          const { data: gamesData } = await supabase
            .from("games")
            .select("home_team, away_team, game_status, game_complete, home_score, away_score, period")
            .eq("year", SEASON_YEAR).eq("week", weekNum.toString());
          const gamesByTeam = buildGamesByTeam(gamesData);
          setScheduleData(gamesByTeam);
          return { gamesByTeam, rawGames: gamesData || [] };
        } catch (error) {
          console.error("Error fetching schedule data:", error);
          return { gamesByTeam: {}, rawGames: [] };
        } finally {
          setScheduleLoading(false);
        }
      };

      const loadAvailableWeeks = async () => {
        try {
          const { data: standingsData } = await supabase
            .from("weekly_standings").select("week").eq("league_id", leagueId);
          if (standingsData && standingsData.length > 0) {
            const weeks = [...new Set(standingsData.map((row) => parseInt(row.week)))].sort((a, b) => a - b);
            setAvailableWeeks(weeks);
            return weeks;
          }
          return [];
        } catch (error) {
          console.error("Error loading available weeks:", error);
          return [];
        }
      };

      // ── main fetch ──────────────────────────────────────────────────────────
      try {
        if (ctxLeagueData) {
          setLeagueName(ctxLeagueData.name || "League");
          setMaxManagers(ctxLeagueData.max_managers || 8);
        }

        const weekNum = getCurrentWeekNumber(currentWeek);
        const cacheKey = `leagueData:${leagueId}:week:${weekNum}`;

        // Served from the last visit if it is still inside its TTL, so
        // switching tabs and coming back does not refetch the whole page.
        const cached = getCached(cacheKey);
        if (cached) {
          setScheduleData(cached.scheduleData);
          setAvailableWeeks(cached.availableWeeks);
          setMembers(maskBlockedMembers(cached.members, blockedIds));
          setScheduleLoading(false);
          setLoading(false);
          if (weekNum > 1) loadWeeklyStandings(weekNum - 1, maskBlockedMembers(cached.members, blockedIds));
          return;
        }

        // Schedule, available weeks, members and this week's lineups are all
        // independent of each other. Only the users lookup below depends on
        // member ids, so running them together instead of one after another
        // cuts the page's network wait roughly in half.
        const schedulePromise = currentWeek && currentWeek !== "Preseason"
          ? fetchScheduleData(currentWeek)
          : Promise.resolve(setScheduleLoading(false));

        const [scheduleResult, weeksResult, { data: membersSnapshot }, { data: lineupRows }] = await Promise.all([
          schedulePromise,
          loadAvailableWeeks(),
          supabase.from("league_members").select("*").eq("league_id", leagueId),
          // Not filtered to weekNum: the standings tiebreak needs each
          // member's best week across the whole season, not just this one.
          supabase
            .from("weekly_lineups")
            .select("member_id, week, captain, trip_play_team, points, starters, bench, team_points")
            .eq("league_id", leagueId),
        ]);

        const visibleMembers = maskBlockedMembers(membersSnapshot || [], blockedIds);

        const memberUserIds = (membersSnapshot || [])
          .map((m) => m.user_id)
          .filter(Boolean);
        const { data: usersData } = await supabase
          .from("users")
          .select("id, first_name")
          .in("id", memberUserIds);
        const userFirstNameMap = Object.fromEntries(
          (usersData || []).map((u) => [u.id, u.first_name || "Unknown"])
        );

        // captain, trip_play_team and this week's points are NOT on
        // league_members. They live on weekly_lineups, one row per member per
        // week. Reading them off the member row returned undefined every time,
        // which silently disabled the captain crown, the 5x badge and its row
        // highlight, and pinned the "Week" column to 0 for everyone.

        const lineupByMemberId = Object.fromEntries(
          (lineupRows || [])
            .filter((row) => row.week === weekNum)
            .map((row) => [row.member_id, row])
        );

        const seasonRowsByMemberId = {};
        (lineupRows || []).forEach((row) => {
          (seasonRowsByMemberId[row.member_id] = seasonRowsByMemberId[row.member_id] || []).push(row);
        });

        const membersData = visibleMembers.map((memberData) => {
          const lineup = lineupByMemberId[memberData.id] || {};
          const { bestWeekPoints, bestWeekCaptainPoints } = getBestWeekStats(seasonRowsByMemberId[memberData.id]);
          return {
            ...memberData,
            // `id` is league_members.id, NOT user_id. It reads as user_id in
            // the old code, but the `...memberData` spread came last and
            // overrode it, so every consumer has always received
            // league_members.id. Keeping that identity: PlayoffBracket and the
            // rank-change lookup both match on it, and it is also what
            // weekly_lineups and weekly_standings reference. userId is exposed
            // separately for anything that genuinely needs the user row.
            id: memberData.id,
            memberId: memberData.id,
            userId: memberData.user_id,
            firstName: memberData.blocked
              ? BLOCKED_TEAM_NAME
              : userFirstNameMap[memberData.user_id] || "Unknown",
            captain: lineup.captain || null,
            tripPlayTeam: lineup.trip_play_team || null,
            // Earned points per team, captain and 5x multipliers applied, as
            // backend/scoring.js writes them. Read by the League tab's starters
            // panel; without it every finished game there showed a dash.
            teamPoints: lineup.team_points || {},
            weeklyPoints: lineup.points ?? 0,
            // Second and third-stage standings tiebreak, after season points:
            // the manager's best single week all season, and the captain's
            // earned points in that same week. See getBestWeekStats.
            bestWeekPoints,
            bestWeekCaptainPoints,
            // getMemberLiveStatus and getStartersPlayedCount both read
            // member.lineup.starters. Neither field was ever populated here,
            // so the League tab's live indicator has silently always
            // evaluated to false since it was written.
            lineup: { starters: lineup.starters || [], bench: lineup.bench || [] },
            // Every week's lineup, keyed by week, for the same panel when a
            // past week is selected. Already in lineupRows, which is fetched
            // for the whole season for the tiebreak, so this costs no query.
            lineupsByWeek: Object.fromEntries(
              (seasonRowsByMemberId[memberData.id] || []).map((row) => [row.week, {
                starters: row.starters || [],
                captain: row.captain || null,
                tripPlayTeam: row.trip_play_team || null,
                teamPoints: row.team_points || {},
              }])
            ),
            hasTripPlay: memberData.has_trip_play || false,
            tripPlayUsedWeek: memberData.trip_play_used_week || null,
            freezesRemaining: memberData.freezes_remaining ?? 0,
            teamAvatar: memberData.avatar_url || null,
            points: memberData.points || 0,
            teamName: memberData.team_name || "Unnamed Team",
          };
        });

        setMembers(membersData);
        setLoading(false);

        // Held for 2 minutes while any game in this week is in progress (the
        // interval backend/espn.js ingests on, so nothing newer exists to
        // fetch), and 5 minutes when nothing is live and none of this can
        // change.
        const ttl = ttlForGames(scheduleResult?.rawGames);
        setCached(
          cacheKey,
          {
            scheduleData: scheduleResult?.gamesByTeam || {},
            availableWeeks: weeksResult || [],
            members: membersData,
          },
          ttl
        );
        // Only entries written during a live window expire on app resume.
        if (ttl === LIVE_TTL_MS) markLive(cacheKey);

        // Prefetch last week so the rank-change arrows have something to
        // compare against. It runs after members are built and is handed them
        // directly: `members` state is still the empty initial array at this
        // point, and a week cached without them would keep its blank names for
        // the rest of the session.
        if (weekNum > 1) loadWeeklyStandings(weekNum - 1, membersData);
      } catch (error) {
        console.error("Error fetching data:", error);
        setLoading(false);
        setScheduleLoading(false);
      }
    };

    fetchData();
    // intentional: only re-run when leagueId changes (or the viewer's block
    // list does); schedule/members data is stable per league
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leagueId, blockKey]);

  // The playoff bracket is no longer fetched here. It was read from the
  // `playoffs` table, which only had a row once an admin ran
  // initializePlayoffs, and it keyed managers by user_id while this page keys
  // them by league_members.id, so it could not resolve them anyway. The
  // bracket is now derived from the standings and weekly_standings by
  // src/utils/playoffs.js and rendered by PlayoffPicture.


  return {
    members,
    leagueName,
    maxManagers,
    loading,
    scheduleData,
    scheduleLoading,
    weeklyStandings,
    availableWeeks,
    standingsLoading,
    loadWeeklyStandings,
  };
}
